import {
  conversationIdToSessionId,
  UnifiedParticipantsData,
  UnifiedSessionHint,
} from 'services/chatUnifiedService';

export interface StudentInfo {
  id: number;
  name: string;
  yearFinishHighSchool?: number;
  phoneNumber?: string;
}

export interface ChatParticipant {
  id: number;
  name: string;
  email?: string;
  avatar?: string;
  phoneNumber?: string;
  is_phone_verify?: boolean;
  type: 'student' | 'counselor' | 'parent';
  partnerId?: number;
  /** Parent's user id — SimpleChatBox uses this for to_id when messaging a parent. */
  parentId?: number;
  counselorId?: number;
  studentId?: number;
  relationship?: string;
  yearFinishHighSchool?: number;
  sessionId?: string;
  studentInfo?: StudentInfo;
  subscriptionPlan?: string;
  initialMessageId?: number;
  lastMessagePreview?: string;
  lastMessageSentBy?: number;
  lastMessageStatus?: string;
}

function userIdFromRecord(row: any): number | undefined {
  const n = Number(row?.userId ?? row?.partner_id ?? row?.student_id ?? row?.id);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Display name from flat BE profile or nested legacy shapes. */
function nameFromRecord(row: any): string {
  const info = row?.partner_info ?? row?.student_info ?? row;
  const fn = info?.first_name ?? row?.first_name;
  const mn = info?.middle_name ?? row?.middle_name;
  const ln = info?.last_name ?? row?.last_name;
  const parts = [fn, mn, ln].filter(Boolean);
  if (parts.length) return parts.join(' ').replace(/\s+/g, ' ').trim();
  return 'User';
}

function yearFromStudentRow(row: any): number | undefined {
  const si = row?.student_info ?? row;
  const y =
    si?.yearFinishHighSchool ??
    row?.yearFinishHighSchool ??
    si?.year_finish_high_school ??
    row?.year_finish_high_school;
  const n = Number(y);
  return Number.isFinite(n) ? n : undefined;
}

/** Counselor parent rows: BE `linked_student_*` = managed student display name. */
function managedStudentNameFromParentRow(row: any): string | undefined {
  const fn = row?.linked_student_first_name;
  const mn = row?.linked_student_middle_name;
  const ln = row?.linked_student_last_name;
  const parts = [fn, mn, ln].filter((p) => p != null && String(p).trim() !== '');
  if (!parts.length) return undefined;
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

/** Prefer BE `session_hint` on each row; fallback to matching `meta.session_hints`. */
function sessionIdFromEmbeddedHint(row: any): string | undefined {
  const raw = row?.session_hint?.conversation_id;
  if (typeof raw !== 'string' || !raw.trim()) return undefined;
  return conversationIdToSessionId(raw);
}

function lastMsgFromRow(row: any): {
  lastMessagePreview?: string;
  lastMessageSentBy?: number;
  lastMessageStatus?: string;
} {
  const preview = row?.lastMessagePreview ?? row?.last_message_preview;
  const sentBy = row?.lastMessageSentBy ?? row?.last_message_sent_by;
  const status = row?.lastMessageStatus ?? row?.last_message_status;
  return {
    lastMessagePreview: typeof preview === 'string' && preview ? preview : undefined,
    lastMessageSentBy: sentBy != null && Number.isFinite(Number(sentBy)) ? Number(sentBy) : undefined,
    lastMessageStatus: typeof status === 'string' && status ? status : undefined,
  };
}

/**
 * Resolve session id (e.g. "20-39") for two participants using `pair` + ids in hints.
 */
export function resolveSessionIdFromHints(
  hints: UnifiedSessionHint[] | undefined,
  id1: number,
  id2: number
): string | undefined {
  const a = id1;
  const b = id2;
  for (const h of hints || []) {
    const sid = conversationIdToSessionId(h.conversation_id);
    switch (h.pair) {
      case 'student_counselor':
        if (
          h.student_id != null &&
          h.counselor_id != null &&
          ((h.student_id === a && h.counselor_id === b) ||
            (h.student_id === b && h.counselor_id === a))
        ) {
          return sid;
        }
        break;
      case 'student_parent':
        if (
          h.student_id != null &&
          h.parent_id != null &&
          ((h.student_id === a && h.parent_id === b) || (h.student_id === b && h.parent_id === a))
        ) {
          return sid;
        }
        break;
      case 'parent_counselor':
        if (
          h.parent_id != null &&
          h.counselor_id != null &&
          ((h.parent_id === a && h.counselor_id === b) ||
            (h.parent_id === b && h.counselor_id === a))
        ) {
          return sid;
        }
        break;
      default:
        break;
    }
  }
  return undefined;
}

/** Map parent_user_id -> student ids from student_parent hints (for counselor parent rows). */
function parentToStudentIdsFromHints(hints: UnifiedSessionHint[], parentUserId: number): number[] {
  const out: number[] = [];
  for (const h of hints) {
    if (h.pair === 'student_parent' && h.parent_id === parentUserId && h.student_id != null) {
      out.push(h.student_id);
    }
  }
  return [...new Set(out)];
}

export function buildUnifiedParticipantLists(
  role: 'student' | 'counselor' | 'parent',
  data: UnifiedParticipantsData,
  ctx: {
    userId: number;
    studentId: number | undefined;
    hints: UnifiedSessionHint[];
    isIgnoredStudent: (id?: number, email?: string) => boolean;
  }
): {
  counselors: ChatParticipant[];
  parents: ChatParticipant[];
  students: ChatParticipant[];
  sessionIds: string[];
} {
  const { userId, studentId, hints, isIgnoredStudent } = ctx;

  if (role === 'student') {
    const sidSelf = studentId ?? userId;
    const counselors: ChatParticipant[] = [];
    const parents: ChatParticipant[] = [];

    for (const counselor of data.counselors || []) {
      if (counselor?.requested_status && counselor.requested_status !== 'approved') continue;
      const counselorUserId = userIdFromRecord(counselor);
      if (!counselorUserId) continue;
      const sessionId =
        sessionIdFromEmbeddedHint(counselor) ??
        resolveSessionIdFromHints(hints, sidSelf, counselorUserId);
      if (!sessionId) continue;
      const info = counselor.partner_info ?? counselor;
      counselors.push({
        id: counselorUserId,
        name: nameFromRecord(counselor),
        email: info.email ?? counselor.email,
        avatar: info.profile_picture ?? counselor.profile_picture,
        type: 'counselor',
        counselorId: counselorUserId,
        sessionId,
        phoneNumber: info.phone_number ?? counselor.phone_number,
        is_phone_verify: info.is_phone_verify ?? counselor.is_phone_verify,
        subscriptionPlan: info.subscription_plan ?? counselor.subscription_plan,
        ...lastMsgFromRow(counselor),
      });
    }

    for (const parent of data.parents || []) {
      if (parent?.requested_status && parent.requested_status !== 'approved') continue;
      const parentUserId = userIdFromRecord(parent);
      if (!parentUserId) continue;
      const sessionId =
        sessionIdFromEmbeddedHint(parent) ??
        resolveSessionIdFromHints(hints, sidSelf, parentUserId);
      if (!sessionId) continue;
      const info = parent.partner_info ?? parent;
      parents.push({
        id: parentUserId,
        parentId: parentUserId,
        name: nameFromRecord(parent),
        email: info.email ?? parent.email,
        avatar: info.profile_picture ?? parent.profile_picture,
        type: 'parent',
        partnerId: parentUserId,
        relationship: info.relationship ?? parent.relationship,
        sessionId,
        phoneNumber: info.phone_number ?? parent.phone_number,
        is_phone_verify: info.is_phone_verify ?? parent.is_phone_verify,
        ...lastMsgFromRow(parent),
      });
    }

    return {
      counselors,
      parents,
      students: [],
      sessionIds: collectSessionIdsFromUnifiedData(data),
    };
  }

  if (role === 'counselor') {
    const students: ChatParticipant[] = [];
    const parents: ChatParticipant[] = [];

    for (const student of data.students || []) {
      if (student?.requested_status && student.requested_status !== 'approved') continue;
      const sid = userIdFromRecord(student) ?? student.student_id;
      const si = student.student_info;
      const emailFlat = student.email ?? si?.email;
      if (!sid || isIgnoredStudent(sid, emailFlat)) continue;
      const sessionId =
        sessionIdFromEmbeddedHint(student) ??
        resolveSessionIdFromHints(hints, sid, userId);
      if (!sessionId) continue;
      students.push({
        id: sid,
        name: nameFromRecord(student),
        email: emailFlat,
        avatar: si?.profile_picture ?? student.profile_picture,
        type: 'student',
        studentId: sid,
        yearFinishHighSchool: yearFromStudentRow(student),
        sessionId,
        phoneNumber: si?.phone_number ?? student.phone_number,
        is_phone_verify: si?.is_phone_verify ?? student.is_phone_verify,
        subscriptionPlan: si?.subscription_plan ?? student.subscription_plan,
        ...lastMsgFromRow(student),
      });
    }

    const studentById = new Map<number, any>();
    for (const s of data.students || []) {
      const sid = userIdFromRecord(s) ?? s.student_id;
      if (sid) studentById.set(sid, s);
    }

    for (const row of data.parents || []) {
      if (row?.requested_status && row.requested_status !== 'approved') continue;
      const parentUserId = userIdFromRecord(row) ?? row.partner_id;
      if (!parentUserId) continue;

      const sessionId =
        sessionIdFromEmbeddedHint(row) ??
        resolveSessionIdFromHints(hints, parentUserId, userId);
      if (!sessionId) continue;

      const candidateStudentIds = parentToStudentIdsFromHints(hints, parentUserId);
      let childId: number | undefined = candidateStudentIds.find((cid) =>
        hints.some(
          (h) =>
            h.pair === 'student_counselor' &&
            h.student_id === cid &&
            h.counselor_id === userId
        )
      );
      if (childId == null) childId = candidateStudentIds[0];
      if (childId == null) {
        const stRow = (data.students || []).find((s: any) => !isIgnoredStudent(userIdFromRecord(s), s?.email));
        childId = userIdFromRecord(stRow ?? {}) ?? stRow?.student_id;
      }

      const stRow = childId != null ? studentById.get(childId) : undefined;
      const stInfo = stRow?.student_info ?? stRow;
      if (childId != null && isIgnoredStudent(childId, stInfo?.email ?? row?.student_info?.email)) continue;

      const pi = row.partner_info || row;
      const managedName = managedStudentNameFromParentRow(row);
      const studentName =
        managedName ||
        (stInfo && stInfo.first_name && stInfo.last_name
          ? nameFromRecord({ ...stInfo, first_name: stInfo.first_name, last_name: stInfo.last_name })
          : stRow
            ? nameFromRecord(stRow)
            : 'Student');

      parents.push({
        id: parentUserId,
        parentId: parentUserId,
        studentId: childId,
        name: nameFromRecord(row),
        email: pi.email ?? row.email,
        avatar: pi.profile_picture ?? row.profile_picture,
        type: 'parent',
        partnerId: row.partner_id ?? parentUserId,
        relationship: pi.relationship ?? row.relationship,
        phoneNumber: pi.phone_number ?? row.phone_number,
        is_phone_verify: pi.is_phone_verify ?? row.is_phone_verify,
        studentInfo: childId
          ? {
              id: childId,
              name: studentName,
              yearFinishHighSchool: yearFromStudentRow(stRow || {}),
              phoneNumber: pi.phone_number,
            }
          : { id: 0, name: 'Student' },
        sessionId,
        subscriptionPlan: pi.subscription_plan ?? row.subscription_plan,
        ...lastMsgFromRow(row),
      });
    }

    return {
      counselors: [],
      parents,
      students,
      sessionIds: collectSessionIdsFromUnifiedData(data),
    };
  }

  // parent
  const students: ChatParticipant[] = [];
  const counselors: ChatParticipant[] = [];

  for (const student of data.students || []) {
    if (student?.requested_status && student.requested_status !== 'approved') continue;
    const sid = userIdFromRecord(student) ?? student.student_id;
    const si = student.student_info;
    const emailFlat = student.email ?? si?.email;
    if (!sid || isIgnoredStudent(sid, emailFlat)) continue;
    const sessionId =
      sessionIdFromEmbeddedHint(student) ??
      resolveSessionIdFromHints(hints, sid, userId);
    if (!sessionId) continue;
    students.push({
      id: sid,
      name: nameFromRecord(student),
      email: emailFlat,
      avatar: si?.profile_picture ?? student.profile_picture,
      type: 'student',
      studentId: sid,
      yearFinishHighSchool: yearFromStudentRow(student),
      sessionId,
      phoneNumber: si?.phone_number ?? student.phone_number,
      is_phone_verify: si?.is_phone_verify ?? student.is_phone_verify,
      subscriptionPlan: si?.subscription_plan ?? student.subscription_plan,
      ...lastMsgFromRow(student),
    });
  }

  const studentRows = (data.students || []).filter((s: any) => {
    if (s?.requested_status && s.requested_status !== 'approved') return false;
    return true;
  });

  const linkedStudentIds = parentToStudentIdsFromHints(hints, userId);

  for (const counselor of data.counselors || []) {
    if (counselor?.requested_status && counselor.requested_status !== 'approved') continue;
    const counselorUserId = userIdFromRecord(counselor);
    if (!counselorUserId) continue;

    const sessionId =
      sessionIdFromEmbeddedHint(counselor) ??
      resolveSessionIdFromHints(hints, userId, counselorUserId);
    if (!sessionId) continue;

    let stId = linkedStudentIds.find((sid) =>
      hints.some(
        (x) =>
          x.pair === 'student_counselor' &&
          x.student_id === sid &&
          x.counselor_id === counselorUserId
      )
    );
    if (stId == null) stId = linkedStudentIds[0];

    const stRow = stId != null ? studentRows.find((s: any) => userIdFromRecord(s) === stId) : undefined;
    const si = stRow?.student_info ?? stRow;

    if (stRow && isIgnoredStudent(userIdFromRecord(stRow) ?? stRow.student_id, si?.email)) {
      continue;
    }

    const defaultStudentName =
      typeof counselor.managed_student_name === 'string' && counselor.managed_student_name.trim()
        ? counselor.managed_student_name.trim()
        : undefined;

    counselors.push({
      id: counselorUserId,
      name: nameFromRecord(counselor),
      email: counselor.email,
      avatar: counselor.profile_picture,
      phoneNumber: counselor.phone_number,
      is_phone_verify: counselor.is_phone_verify,
      type: 'counselor',
      counselorId: counselorUserId,
      studentInfo: stRow
        ? {
            id: userIdFromRecord(stRow) ?? stRow.student_id ?? 0,
            name: nameFromRecord(stRow),
            yearFinishHighSchool: yearFromStudentRow(stRow),
            phoneNumber: '',
          }
        : studentRows[0]
          ? {
              id: userIdFromRecord(studentRows[0]) ?? studentRows[0].student_id ?? 0,
              name: nameFromRecord(studentRows[0]),
              yearFinishHighSchool: yearFromStudentRow(studentRows[0]),
              phoneNumber: '',
            }
          : { id: 0, name: defaultStudentName ?? 'Student' },
      sessionId,
      subscriptionPlan: counselor.subscription_plan,
      ...lastMsgFromRow(counselor),
    });
  }

  return {
    counselors,
    parents: [],
    students,
    sessionIds: collectSessionIdsFromUnifiedData(data),
  };
}

export function collectSessionIdsFromHints(hints: UnifiedSessionHint[] | undefined): string[] {
  if (!hints?.length) return [];
  return [...new Set(hints.map((h) => conversationIdToSessionId(h.conversation_id)))];
}

/** `meta.session_hints` plus any `session_hint` on each row (covers BE embedding-only responses). */
export function collectSessionIdsFromUnifiedData(data: UnifiedParticipantsData): string[] {
  const ids = new Set<string>(collectSessionIdsFromHints(data.meta?.session_hints));
  const addFromRows = (rows: any[] | undefined) => {
    for (const row of rows || []) {
      const sid = sessionIdFromEmbeddedHint(row);
      if (sid) ids.add(sid);
    }
  };
  addFromRows(data.students);
  addFromRows(data.counselors);
  addFromRows(data.parents);
  return [...ids];
}

export function fallbackUnreadFromIds(
  role: 'student' | 'counselor' | 'parent',
  counselors: ChatParticipant[],
  parents: ChatParticipant[],
  students: ChatParticipant[]
): number[] {
  const fromIds: number[] = [];
  if (role === 'counselor' || role === 'parent') {
    students.forEach((s) => s.id && fromIds.push(s.id));
    if (role === 'counselor') {
      parents.forEach((p) => p.id && fromIds.push(p.id));
    } else {
      counselors.forEach((c) => c.id && fromIds.push(c.id));
    }
  } else {
    counselors.forEach((c) => c.id && fromIds.push(c.id));
    parents.forEach((p) => p.id && fromIds.push(p.id));
  }
  return fromIds;
}
